import React, { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import Toaster from 'components/Toaster';
import { AppContext } from 'shared/AppContext';
import { generateSekretKey } from 'shared/generateSecretKey';
import storage from 'shared/storage';
import { formatPhoneForDisplay } from '../components/AuthPhoneInput';
import OtpInput from '../components/OtpInput';
import RequestCode from '../gql/request-code';
import RequestEmailCode from '../gql/request-email-code';
import RequestSignupEmailCode from '../gql/request-signup-email-code';
import VerifyCode from '../gql/verify-code';
import HelpLink from './components/HavingTrouble';
import SplitScreenLayout from './components/SplitScreenLayout';
import { useSignup } from './context/SignupContext';

const PhoneDisplay = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  text-align: center;
  margin-bottom: 24px;
`;

const PhoneNumber = styled.span`
  color: #fff;
  font-size: 19px;
  font-weight: 600;
  line-height: 1;

  @media (max-width: 900px) {
    font-size: 20px;
  }
`;

const EditButton = styled.button`
  background: transparent;
  border: none;
  color: #fa4616;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  @media (max-width: 900px) {
    font-size: 17px;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorText = styled.p`
  color: #ef4444;
  font-size: 14px;
  text-align: center;
  margin: 16px 0;

  @media (max-width: 900px) {
    font-size: 15px;
  }
`;

const ResendLink = styled.button`
  display: block;
  text-align: center;
  color: #fa4616;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin: 16px auto 0;
  background: transparent;
  border: none;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  @media (max-width: 900px) {
    font-size: 15px;
  }
`;

const EmailFallbackLink = styled.button`
  display: block;
  text-align: center;
  color: #fa4616;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin: 20px auto 0;
  background: transparent;
  border: none;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  padding: 12px 0 0;
  width: 100%;

  &:hover {
    text-decoration: underline;
    color: #fa4616;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  @media (max-width: 900px) {
    font-size: 16px;
  }
`;

const InputRow = styled.div`
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 14px 16px;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 18px;
  outline: none;
  font-family: ${(props) =>
    props.theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif'};

  @media (max-width: 900px) {
    font-size: 19px;
  }

  &::placeholder {
    color: #475569;
  }
`;

const ContinueButton = styled.button`
  display: block;
  width: 100%;
  padding: 16px;
  margin-top: 20px;
  background: #fa4616;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  font-family: ${(props) =>
    props.theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #e03e12;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 900px) {
    font-size: 17px;
  }
`;

const HelpHint = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr;
  column-gap: 12px;
  align-items: center;
  text-align: left;
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(30, 41, 59, 0.78);
`;

const HelpHintIcon = styled.span`
  color: #d7e1f3;
  width: 20px;
  height: 20px;
  margin-top: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const HelpHintContent = styled.div`
  flex: 1;
`;

const HelpHintTitle = styled.p`
  margin: 0;
  color: #f8fafc;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;

  @media (max-width: 900px) {
    font-size: 16px;
  }
`;

const HelpHintBody = styled.p`
  margin: 4px 0 0;
  color: #a6b4cb;
  font-size: 13px;
  line-height: 1.5;

  @media (max-width: 900px) {
    font-size: 14px;
  }
`;

const EmailSuccessNotice = styled.div`
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(74, 222, 128, 0.35);
  background: rgba(34, 197, 94, 0.14);
  color: #d1fae5;
  font-size: 13px;
  font-weight: 600;
  text-align: center;

  @media (max-width: 900px) {
    font-size: 14px;
  }
`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const extractRequestErrorMessage = (error, fallbackMessage) => {
  if (!error) return fallbackMessage;
  if (typeof error === 'string') return error;

  const graphQLErrorMessage = error?.graphQLErrors?.[0]?.message;
  if (graphQLErrorMessage) return graphQLErrorMessage;

  const networkErrorMessage = error?.networkError?.result?.errors?.[0]?.message;
  if (networkErrorMessage) return networkErrorMessage;

  if (typeof error?.message === 'string' && error.message.length) {
    return error.message;
  }

  return fallbackMessage;
};

const StepTwo = ({
  verifyCodeV2,
  phoneNumber,
  setStep,
  setShowConfirmation,
  requestCodeV2,
  requestEmailCodeV2,
  requestSignupEmailCode,
  countryCode,
}) => {
  const { setExistingUser, updateFields } = useSignup();
  const [verificationCode, setVerificationCode] = useState([
    '',
    '',
    '',
    '',
    '',
    '',
  ]);
  const [error, setError] = useState('');
  const { dispatch } = useContext(AppContext);
  const [verificationTried, setVerificationTried] = useState(false);
  const [isTrying, setIsTrying] = useState(false);
  const [codeSentVia, setCodeSentVia] = useState('sms');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [emailNotice, setEmailNotice] = useState('');
  const [toaster, setToaster] = useState({
    isOpen: false,
    type: 'error',
    title: '',
    message: '',
  });

  const showErrorToaster = (title, error, fallbackMessage) => {
    setToaster({
      isOpen: true,
      type: 'error',
      title,
      message: extractRequestErrorMessage(error, fallbackMessage),
    });
  };

  const handleEditPhone = () => {
    setShowConfirmation(false);
    setCodeSentVia('sms');
    setMaskedEmail('');
    setShowEmailInput(false);
    setNewUserEmail('');
    setEmailNotice('');
    setError('');
    setVerificationCode(['', '', '', '', '', '']);
  };

  const handleResendCode = async () => {
    setVerificationCode(['', '', '', '', '', '']);
    if (codeSentVia === 'email' && newUserEmail) {
      setEmailLoading(true);
      try {
        const secretKey = await generateSekretKey();
        const result = await requestSignupEmailCode(
          {
            phone: phoneNumber,
            calling_code: countryCode,
            email: newUserEmail,
          },
          secretKey
        );
        if (result?.data?.requestSignupEmailCode?.success) {
          setEmailNotice(`Email code resent to ${newUserEmail}.`);
          toast.success('Code resent to your email.');
        } else {
          showErrorToaster(
            'Unable to resend email code',
            null,
            'Unable to resend email code.'
          );
        }
      } catch (e) {
        showErrorToaster(
          'Unable to resend email code',
          e,
          'Unable to resend email code.'
        );
      }
      setEmailLoading(false);
    } else if (codeSentVia === 'email') {
      setEmailLoading(true);
      try {
        const secretKey = await generateSekretKey();
        const result = await requestEmailCodeV2(
          {
            phone: phoneNumber,
            calling_code: countryCode,
          },
          secretKey
        );
        if (result?.data?.requestEmailCodeV2?.success) {
          setMaskedEmail(result.data.requestEmailCodeV2.masked_email);
          setEmailNotice(
            `Email code resent to ${
              result.data.requestEmailCodeV2.masked_email || 'your email'
            }.`
          );
          toast.success('Code resent to your email.');
        } else {
          showErrorToaster(
            'Unable to resend email code',
            null,
            'Unable to resend email code.'
          );
        }
      } catch (e) {
        showErrorToaster(
          'Unable to resend email code',
          e,
          'Unable to resend email code.'
        );
      }
      setEmailLoading(false);
    } else {
      try {
        const secretKey = await generateSekretKey();
        const result = await requestCodeV2(
          {
            phone: phoneNumber,
            calling_code: countryCode,
          },
          secretKey
        );
        if (result?.data?.requestCodeV2) {
          toast.success('SMS Code resent successfully.');
          storage.set('promoter-user-phone', {
            phone: phoneNumber,
            calling_code: countryCode,
          });
          setStep('StepTwo');
        } else {
          showErrorToaster(
            'Unable to resend code',
            null,
            'Error resending SMS code.'
          );
        }
      } catch (e) {
        showErrorToaster(
          'Unable to resend code',
          e,
          'Error resending SMS code.'
        );
      }
    }
  };

  const handleEmailFallback = async () => {
    if (emailLoading) return;
    setEmailLoading(true);
    try {
      const secretKey = await generateSekretKey();
      const result = await requestEmailCodeV2(
        {
          phone: phoneNumber,
          calling_code: countryCode,
        },
        secretKey
      );
      if (result?.data?.requestEmailCodeV2?.success) {
        setCodeSentVia('email');
        setMaskedEmail(result.data.requestEmailCodeV2.masked_email);
        setVerificationCode(['', '', '', '', '', '']);
        setEmailNotice(
          `Email code sent to ${
            result.data.requestEmailCodeV2.masked_email || 'your email'
          }.`
        );
        toast.success('Code sent to your email.');
      } else {
        setShowEmailInput(true);
      }
    } catch (e) {
      showErrorToaster(
        'Unable to send email code',
        e,
        'Unable to send email code. Please try SMS.'
      );
    }
    setEmailLoading(false);
  };

  const handleSendEmailCode = async () => {
    if (!EMAIL_REGEX.test(newUserEmail)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setEmailLoading(true);
    try {
      const secretKey = await generateSekretKey();
      const result = await requestSignupEmailCode(
        {
          phone: phoneNumber,
          calling_code: countryCode,
          email: newUserEmail,
        },
        secretKey
      );
      if (result?.data?.requestSignupEmailCode?.success) {
        setCodeSentVia('email');
        setMaskedEmail(newUserEmail);
        setShowEmailInput(false);
        setVerificationCode(['', '', '', '', '', '']);
        setEmailNotice(`Email code sent to ${newUserEmail}.`);
        updateFields({ email: newUserEmail });
        toast.success('Code sent to your email.');
      } else {
        showErrorToaster(
          'Unable to send email code',
          null,
          'Unable to send email code. Please try again.'
        );
      }
    } catch (e) {
      showErrorToaster(
        'Unable to send email code',
        e,
        'Unable to send email code. Please try again.'
      );
    }
    setEmailLoading(false);
  };

  const handleVerifyCode = async (values) => {
    const code = values.join('');
    if (!verificationTried && !isTrying) {
      setIsTrying(true);
      const secretKey = await generateSekretKey({
        phone: phoneNumber,
        calling_code: countryCode,
      });

      const result = await verifyCodeV2(code, secretKey);

      if (!result.errors) {
        updateFields({
          phone: phoneNumber,
          calling_code: countryCode,
          country_code: 'US',
        });

        if (result?.data?.verifyCodeV2) {
          setExistingUser(result.data.verifyCodeV2);

          storage.set('user', {
            ...result.data.verifyCodeV2,
            role:
              result.data.verifyCodeV2.role === 'user'
                ? 'track'
                : result.data.verifyCodeV2.role,
          });
          dispatch({
            type: 'SET_THEME',
            theme: 'dark',
          });
          setStep('CreateAccount');
        } else {
          dispatch({
            type: 'SET_THEME',
            theme: 'dark',
          });
          setStep('CreateUser');
          setVerificationTried(true);
        }
      } else {
        setVerificationTried(true);
        setError(result.errors.code);
      }

      setIsTrying(false);
    }
  };

  if (showEmailInput && codeSentVia === 'sms') {
    return (
      <>
        <SplitScreenLayout
          title="Enter Your Email"
          subtitle="You will receive a one time password via email"
        >
          <PhoneDisplay>
            <PhoneNumber>
              {formatPhoneForDisplay(phoneNumber, countryCode)}
            </PhoneNumber>
            <EditButton onClick={handleEditPhone}>Edit</EditButton>
          </PhoneDisplay>

          <InputRow>
            <EmailInput
              type="email"
              placeholder="you@example.com"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
            />
          </InputRow>

          <ContinueButton
            disabled={emailLoading || !newUserEmail}
            onClick={handleSendEmailCode}
          >
            {emailLoading ? 'Sending...' : 'Continue \u2192'}
          </ContinueButton>

          <HelpLink />
        </SplitScreenLayout>

        <Toaster
          isOpen={toaster.isOpen}
          onClose={() => setToaster({ ...toaster, isOpen: false })}
          title={toaster.title}
          message={toaster.message}
          type={toaster.type}
          duration={5000}
        />
      </>
    );
  }

  const emailStepNotice =
    emailNotice ||
    (maskedEmail
      ? `Email verification mode is active. Code sent to ${maskedEmail}.`
      : 'Email verification mode is active. Check your inbox for your code.');

  return (
    <>
      <SplitScreenLayout
        title="Enter One Time Password"
        subtitle={
          codeSentVia === 'email'
            ? `A code was sent to ${maskedEmail}`
            : 'An SMS code was sent to'
        }
      >
        <PhoneDisplay>
          <PhoneNumber>
            {formatPhoneForDisplay(phoneNumber, countryCode)}
          </PhoneNumber>
          <EditButton onClick={handleEditPhone}>Edit</EditButton>
        </PhoneDisplay>

        {codeSentVia === 'email' ? (
          <EmailSuccessNotice role="status" aria-live="polite">
            {emailStepNotice}
          </EmailSuccessNotice>
        ) : null}

        <OtpInput
          variant="split"
          values={verificationCode}
          onChange={(values) => {
            setVerificationCode(values);
            setVerificationTried(false);
            setError('');
          }}
          onComplete={handleVerifyCode}
        />

        {error.length ? <ErrorText>{error}</ErrorText> : null}

        <ResendLink disabled={emailLoading} onClick={handleResendCode}>
          {emailLoading ? 'Sending...' : 'Resend Code'}
        </ResendLink>

        <HelpHint>
          <HelpHintIcon aria-hidden>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 10h.01M12 10h.01M16 10h.01M9 16h6m-9 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </HelpHintIcon>
          <HelpHintContent>
            <HelpHintTitle>Not receiving your code?</HelpHintTitle>
            <HelpHintBody>
              {codeSentVia === 'email'
                ? 'Check your email spam or junk folder.'
                : 'Make sure text message spam filters are turned off on your device.'}
            </HelpHintBody>
          </HelpHintContent>
        </HelpHint>

        {codeSentVia === 'sms' && (
          <EmailFallbackLink
            disabled={emailLoading}
            onClick={handleEmailFallback}
          >
            {emailLoading ? 'Sending...' : 'Send code via email instead'}
          </EmailFallbackLink>
        )}

        <HelpLink />
      </SplitScreenLayout>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ ...toaster, isOpen: false })}
        title={toaster.title}
        message={toaster.message}
        type={toaster.type}
        duration={5000}
      />
    </>
  );
};

export default VerifyCode(
  RequestCode(RequestEmailCode(RequestSignupEmailCode(StepTwo)))
);
